import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IFilter, IOption, sortBy, sortDirection } from "@interfaces";
import { categoriesApi, mapSearchFilterToOptions } from "@api";
import { RootState } from ".";

const initialState: IFilter & { filtersLoaded: boolean; filtersLoading: boolean } = {
    name: null,
    countries: null,
    states: null,
    regions: null,
    localities: null,
    accommodation: [],
    activities: [],
    transports: [],
    suppliers: [],
    destinations: [],
    startLocations: [],
    day: 0,
    duration: null,
    categories: {
        countries: [],
        states: [],
        regions: [],
        localities: [],
        accommodation: [],
        activities: [],
        transports: [],
        suppliers: [],
        destinations: [],
        startLocations: [],
        duration: [],
    },
    filtersLoaded: false,
    filtersLoading: false,
};

export const fetchSearchFilters = createAsyncThunk(
    "searchCategories/fetchSearchFilters",
    async (_, { rejectWithValue }) => {
        try {
            const res = await categoriesApi.getSearchFilters();
            const data = res.data?.data ?? res.data;
            const destinations = mapSearchFilterToOptions(data.destination || []);
            const startLocations = mapSearchFilterToOptions(data.start_location || []);
            return {
                accommodation: mapSearchFilterToOptions(data.accommodation || []),
                activities: mapSearchFilterToOptions(data.activity || []),
                transports: mapSearchFilterToOptions(data.transport || []),
                suppliers: mapSearchFilterToOptions(data.supplier || []),
                countries: mapSearchFilterToOptions(data.country || []),
                destinations,
                startLocations,
                duration: mapSearchFilterToOptions(data.duration || []).sort((a, b) => Number(a.id) - Number(b.id)),
            };
        } catch (err: any) {
            return rejectWithValue(err.response?.data || err.message);
        }
    },
);

const searchCategories = createSlice({
    name: "searchCategories",
    initialState,
    reducers: {
        resetSortDirection: (state) => {
            state.sortDirection = null;
        },
        setName: (state, action) => {
            state.name = action.payload;
        },
        setCountries: (state, action) => {
            state.countries = action.payload;
        },
        setStates: (state, action) => {
            state.states = action.payload;
        },
        setRegions: (state, action) => {
            state.regions = action.payload;
        },
        setTowns: (state, action) => {
            state.localities = action.payload;
        },
        setAccommodation: (state, action: PayloadAction<IOption[]>) => {
            state.accommodation = action.payload;
        },
        setActivity: (state, action: PayloadAction<IOption[]>) => {
            state.activities = action.payload;
        },
        setTransport: (state, action: PayloadAction<IOption[]>) => {
            state.transports = action.payload;
        },
        setSupplier: (state, action: PayloadAction<IOption[]>) => {
            state.suppliers = action.payload;
        },
        setDestinations: (state, action: PayloadAction<IOption[]>) => {
            state.destinations = action.payload;
        },
        setStartLocations: (state, action: PayloadAction<IOption[]>) => {
            state.startLocations = action.payload;
        },
        setDay: (state, action) => {
            state.day = action.payload;
        },
        setLengthOfTrip: (state, action) => {
            state.duration = action.payload;
        },
        setCountriesCategory: (state, action) => {
            state.categories.countries = action.payload;
        },
        setStatesCategory: (state, action) => {
            state.categories.states = action.payload;
        },
        setRegionsCategory: (state, action) => {
            state.categories.regions = action.payload;
        },
        setTownsCategory: (state, action) => {
            state.categories.localities = action.payload;
        },
        setActivityCategory: (state, action: PayloadAction<IOption[]>) => {
            state.categories.activities = action.payload;
        },
        setAccommodationCategory: (state, action: PayloadAction<IOption[]>) => {
            state.categories.accommodation = action.payload;
        },
        setTransportCategory: (state, action: PayloadAction<IOption[]>) => {
            state.categories.transports = action.payload;
        },
        setSupplierCategory: (state, action: PayloadAction<IOption[]>) => {
            state.categories.suppliers = action.payload;
        },
        setLengthOfTripCategory: (state, action) => {
            state.categories.duration = action.payload;
        },
        setSortBy: (state, action: PayloadAction<sortBy>) => {
            state.sortBy = action.payload;
        },
        setSortDirection: (state, action: PayloadAction<sortDirection>) => {
            state.sortDirection = action.payload;
        },
        removeExceptCountry: (state) => {
            state.states = null;
            state.categories.states = [];
            state.regions = null;
            state.categories.regions = [];
            state.localities = null;
            state.categories.localities = [];
            state.accommodation = [];
            state.categories.accommodation = [];
            state.activities = [];
            state.categories.activities = [];
            state.transports = [];
            state.categories.transports = [];
        },
        removeExceptState: (state) => {
            state.regions = null;
            state.categories.regions = [];
            state.localities = null;
            state.categories.localities = [];
            state.accommodation = [];
            state.categories.accommodation = [];
            state.activities = [];
            state.categories.activities = [];
            state.transports = [];
            state.categories.transports = [];
        },
        removeExceptRegions: (state) => {
            state.localities = null;
            state.categories.localities = [];
            state.accommodation = [];
            state.categories.accommodation = [];
            state.activities = [];
            state.categories.activities = [];
            state.transports = [];
            state.categories.transports = [];
        },
        removeExceptTowns: (state) => {
            state.accommodation = [];
            state.categories.accommodation = [];
            state.activities = [];
            state.categories.activities = [];
            state.transports = [];
            state.categories.transports = [];
        },
        resetSearch: () => {
            return { ...initialState, categories: { ...initialState.categories } };
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSearchFilters.pending, (state) => {
                state.filtersLoading = true;
            })
            .addCase(fetchSearchFilters.fulfilled, (state, action) => {
                state.filtersLoading = false;
                state.filtersLoaded = true;
                state.categories.accommodation = action.payload.accommodation;
                state.categories.activities = action.payload.activities;
                state.categories.transports = action.payload.transports;
                state.categories.suppliers = action.payload.suppliers;
                if (action.payload.countries?.length) {
                    state.categories.countries = action.payload.countries;
                }
                if (action.payload.destinations?.length) {
                    state.categories.destinations = action.payload.destinations;
                    state.categories.regions = action.payload.destinations;
                }
                if (action.payload.startLocations?.length) {
                    state.categories.startLocations = action.payload.startLocations;
                }
                if (action.payload.duration?.length) {
                    state.categories.duration = action.payload.duration;
                }
            })
            .addCase(fetchSearchFilters.rejected, (state) => {
                state.filtersLoading = false;
            });
    },
});

export const selectSearchCategories = (state: RootState) => state.searchCategories;

export const {
    resetSortDirection,
    setName,
    setCountries,
    setStates,
    setRegions,
    setTowns,
    setAccommodation,
    setActivity,
    setTransport,
    setSupplier,
    setDestinations,
    setStartLocations,
    setDay,
    setLengthOfTrip,
    setCountriesCategory,
    setStatesCategory,
    setRegionsCategory,
    setTownsCategory,
    setActivityCategory,
    setAccommodationCategory,
    setTransportCategory,
    setSupplierCategory,
    setLengthOfTripCategory,
    setSortBy,
    setSortDirection,
    removeExceptCountry,
    removeExceptState,
    removeExceptRegions,
    removeExceptTowns,
    resetSearch,
} = searchCategories.actions;

export default searchCategories.reducer;
